// @ds/actions/useActions — action-monitor types, context object, hook.
//
// Split out of `ActionContext.tsx` so that module exports components only.
import { createContext, useContext } from 'react';

export interface ActionStep {
  id: string;
  label: string;
  status: 'pending' | 'success' | 'error';
  message?: string;
  progress?: number;
}

export interface ActionResultLink {
  label: string;
  url?: string;
  onClick?: () => void;
}

export interface Action {
  id: string;
  type: string;
  label: string;
  description?: string;
  status: 'pending' | 'completed' | 'error';
  error?: string;
  createdAt: Date;
  completedAt?: Date;
  steps?: ActionStep[];
  metadata?: Record<string, unknown>;
  result?: unknown;
  resultLink?: ActionResultLink;
}

export interface ActionContextType {
  actions: Action[];
  registerAction: (
    type: string,
    label: string,
    stepLabels?: string[],
    metadata?: Record<string, unknown>,
    description?: string,
    resultLink?: ActionResultLink
  ) => string;
  updateActionStep: (
    actionId: string,
    stepId: string,
    status: ActionStep['status'],
    message?: string,
    progress?: number
  ) => void;
  completeAction: (actionId: string, result?: unknown, resultLink?: ActionResultLink) => void;
  failAction: (actionId: string, error: string, resultLink?: ActionResultLink) => void;
  removeAction: (id: string) => void;
  clearCompleted: () => void;
  getAction: (id: string) => Action | undefined;
  isActionInProgress: (type: string, metadata?: Record<string, unknown>) => boolean;
}

export const ActionContext = createContext<ActionContextType | undefined>(undefined);

export const useActions = () => {
  const context = useContext(ActionContext);
  if (!context) {
    throw new Error('useActions must be used within ActionProvider');
  }
  return context;
};